import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { playerApi, type RankingResponse } from '../../api/playerApi';
import { useAuthStore } from '../../auth/authStore';
import { useTranslation } from '../../i18n/useTranslation';

export function RankingPage() {
  const navigate = useNavigate();
  const { token, user } = useAuthStore();
  const { t, language, setLanguage } = useTranslation();
  const [data, setData] = useState<RankingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFlipping, setIsFlipping] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    if (!token) return;
    // Não colocar loading = true se estiver a virar a página
    if (!isFlipping) setLoading(true);
    
    playerApi.getRanking(token, page, limit)
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, page]);

  const turnPage = (newPage: number) => {
    if (page === newPage) return;
    setIsFlipping(true);
    setTimeout(() => {
      setPage(newPage);
      setTimeout(() => setIsFlipping(false), 50);
    }, 400); // tempo de fechar a folha
  };

  return (
    <div className="gameLayout ranking-page-root">
      <style>{`
        @keyframes expandFromButton {
          0% {
            clip-path: circle(0px at calc(100% - 200px) 40px);
            opacity: 0;
            transform: scale(0.95);
          }
          100% {
            clip-path: circle(150% at calc(100% - 200px) 40px);
            opacity: 1;
            transform: scale(1);
          }
        }

        .ranking-page-root {
          display: flex;
          flex-direction: column;
          padding: 30px 40px;
          gap: 24px;
          overflow-y: auto;
          animation: expandFromButton 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
          transform-origin: calc(100% - 200px) 40px;
        }

        .rankingHeader {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }

        .headerActions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        @media (max-width: 600px) {
          .ranking-page-root {
            padding: 16px;
            /* Fakes a modal on mobile */
            background: rgba(0, 0, 0, 0.8);
            position: fixed;
            inset: 0;
            z-index: 9999;
          }
          
          .rankingHeader {
            flex-direction: column;
            gap: 16px;
            background: linear-gradient(145deg, #181510, #110e0a);
            padding: 16px;
            border-radius: 12px 12px 0 0;
            border: 1px solid rgba(200, 160, 60, 0.3);
            border-bottom: none;
            margin-bottom: -24px;
            box-shadow: 0 -4px 16px rgba(0,0,0,0.5);
            z-index: 2;
          }

          .rankingHeader h1 {
            font-size: 24px !important;
          }

          .headerActions {
            width: 100%;
            justify-content: space-between;
          }

          .exitBtn {
            position: absolute;
            top: 16px;
            right: 16px;
            padding: 6px 12px;
            background: rgba(255, 60, 60, 0.1);
            color: #ff6b6b;
            border: 1px solid rgba(255, 60, 60, 0.3);
            border-radius: 6px;
          }

          .rankingCard {
            border-radius: 0 0 12px 12px !important;
            border-top: none !important;
          }
        }

        .ranking-transition-wrapper {
          perspective: 1000px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .ranking-transition-inner {
          transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s ease;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .ranking-transition-inner.flipping {
          transform: translateY(10px) scale(0.98);
          opacity: 0;
        }

        .rankingCard {
          background: rgba(10, 8, 6, 0.85);
          border: 1px solid rgba(200, 160, 60, 0.3);
          border-radius: 12px;
          overflow-x: auto;
          box-shadow: 0 8px 32px rgba(0,0,0,0.6);
        }

        .rankingTable {
          width: 100%;
          border-collapse: collapse;
        }

        .rankingTable th {
          text-align: left;
          padding: 14px 18px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(200, 160, 60, 0.8);
          border-bottom: 1px solid rgba(200, 160, 60, 0.2);
          background: rgba(0,0,0,0.4);
        }

        .rankingTable td {
          padding: 16px 18px;
          font-size: 14px;
          border-bottom: 1px solid rgba(255,255,255,0.03);
          transition: background 0.15s;
        }

        .rankingTable tr:hover td {
          background: rgba(200, 160, 60, 0.05);
        }

        .rankingTable tr.is-me td {
          background: rgba(200, 160, 60, 0.1);
          color: #e8c87a;
          font-weight: 600;
        }

        .rankIcon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          font-size: 14px;
          font-weight: 900;
          background: rgba(255,255,255,0.05);
          color: #888;
        }

        .rank1 { background: linear-gradient(135deg, #e8c87a, #c8a040); color: #000; box-shadow: 0 0 10px rgba(232,200,122,0.5); }
        .rank2 { background: linear-gradient(135deg, #e0e0e0, #999999); color: #000; }
        .rank3 { background: linear-gradient(135deg, #cd7f32, #8b4513); color: #fff; }

        .paginationBtn {
          background: rgba(200, 160, 60, 0.1);
          border: 1px solid rgba(200, 160, 60, 0.3);
          color: #e8c87a;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          transition: all 0.2s;
        }

        .paginationBtn:hover:not(:disabled) {
          background: rgba(200, 160, 60, 0.25);
          border-color: rgba(200, 160, 60, 0.6);
        }

        .paginationBtn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          border-color: rgba(255,255,255,0.1);
          color: #666;
        }
      `}</style>

      {/* ── HEADER ── */}
      <header className="rankingHeader">
        <div>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 900, letterSpacing: '0.05em', color: '#e8c87a', textTransform: 'uppercase' }}>
            🏆 Leaderboard
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-dim)', letterSpacing: '0.03em' }}>
            {data && !data.showAll && data.adminLimit 
              ? t('ranking.topWarriors').replace('{n}', String(data.adminLimit))
              : t('ranking.global')}
          </p>
        </div>
        <div className="headerActions">
          {/* Language toggle */}
          <div style={{ display: 'flex', gap: 4 }}>
            {(['en', 'pt'] as const).map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                style={{
                  background: language === lang ? 'rgba(200,160,60,0.3)' : 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: language === lang ? '#fff' : '#ccc',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          <button 
            className="exitBtn"
            onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/user')}
          >
            ✖ {t('common.cancel')}
          </button>
        </div>
      </header>

      {/* ── CONTENT ── */}
      <div className="ranking-transition-wrapper">
        <div className={`ranking-transition-inner ${isFlipping ? 'flipping' : ''}`}>
          
          {loading && !isFlipping ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div className="loadingDot" style={{ borderColor: 'rgba(200,160,60,0.2)', borderTopColor: '#e8c87a' }} />
              <p style={{ marginTop: 24, fontSize: 13, color: 'var(--text-dim)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {t('common.loading')}
              </p>
            </div>
          ) : error ? (
            <div className="alertError">{error}</div>
          ) : (
            <div className="rankingCard">
              <table className="rankingTable">
                <thead>
                  <tr>
                    <th style={{ width: 80, textAlign: 'center' }}>Rank</th>
                    <th>{t('ranking.hero')}</th>
                    <th>{t('user.class')}</th>
                    <th style={{ textAlign: 'center' }}>{t('ranking.wins')}</th>
                    <th style={{ textAlign: 'center' }}>{t('ranking.losses')}</th>
                    <th style={{ textAlign: 'right' }}>{t('ranking.score')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.leaderboard.map((entry) => (
                    <tr key={entry.id} className={entry.isMe ? 'is-me' : ''}>
                      <td style={{ textAlign: 'center' }}>
                        <div className={`rankIcon ${entry.rank === 1 ? 'rank1' : entry.rank === 2 ? 'rank2' : entry.rank === 3 ? 'rank3' : ''}`}>
                          {entry.rank}
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {entry.name}
                        {entry.isMe && (
                          <span style={{ marginLeft: 12, fontSize: 10, padding: '2px 8px', background: 'rgba(68,204,136,0.15)', color: '#44cc88', border: '1px solid rgba(68,204,136,0.3)', borderRadius: 12 }}>
                            {t('ranking.you')}
                          </span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-dim)' }}>{t(`class.${entry.class}` as any) || entry.class}</td>
                      <td style={{ textAlign: 'center', color: '#44cc88' }}>{entry.wins}</td>
                      <td style={{ textAlign: 'center', color: '#cc4444' }}>{entry.losses}</td>
                      <td style={{ textAlign: 'right', fontWeight: 900, color: '#e8c87a', fontSize: 16 }}>
                        {entry.totalPoints.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {data?.leaderboard.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 60, color: 'var(--text-dim)' }}>
                        {t('ranking.noHeroes')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── PAGINATION ── */}
          {data && data.totalItems > limit && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24, marginTop: 24 }}>
              <button 
                className="paginationBtn" 
                disabled={page === 1 || isFlipping}
                onClick={() => turnPage(page - 1)}
              >
                « {t('ranking.prev')}
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-dim)', letterSpacing: '0.05em' }}>
                {t('ranking.page')} <strong style={{ color: '#e8c87a' }}>{page}</strong> {t('ranking.of')} {Math.ceil(data.totalItems / limit)}
              </span>
              <button 
                className="paginationBtn" 
                disabled={page >= Math.ceil(data.totalItems / limit) || isFlipping}
                onClick={() => turnPage(page + 1)}
              >
                {t('ranking.next')} »
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
